import { axiosBaseQuery } from "@king-ict/eupisi-ui/utils";
import { createApi, skipToken } from "@reduxjs/toolkit/query/react";

import { Institution } from "../models";
import { ReligiousAndMinorityInstitutionsDto } from "../models/Institution/ReligiousAndMinorityInstitutionsDto";
import { InstitutionChangeReason } from "../models/Request/Request";

const institutionsUrlPrefix: string = "institutions";

export interface IGetInstitutionQueryModel {
  institutionId: number;
  schoolYear: number;
  isOnlyActive?: boolean;
}

interface IInstitutionChangeReason {
  id: InstitutionChangeReason;
  name: string;
}

export interface IGetFirstAssignedInstitutionQueryModel {
  schoolCandidateId?: number | null;
  schoolYearId?: number | null;
  hasMinorityOrReligiousSchool: boolean;
}

interface IGetReligiousAndMinorityInstitutionsQueryModel {
  countyId: number;
  institutionToExcludeId: number;
}

export const institutionApi = createApi({
  reducerPath: "institutionApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["Institutions"],
  endpoints: (build) => ({
    getInstitutionDetails: build.query<Institution, IGetInstitutionQueryModel>({
      query: ({ institutionId, schoolYear, isOnlyActive }) => {
        const baseUrl = `${institutionsUrlPrefix}/get/${institutionId}/${schoolYear}`;

        return isOnlyActive ? `${baseUrl}/${isOnlyActive}` : baseUrl;
      },
    }),
    getReligiousAndMinorityInstitutions: build.query<
      ReligiousAndMinorityInstitutionsDto,
      IGetReligiousAndMinorityInstitutionsQueryModel
    >({
      query: ({ countyId, institutionToExcludeId }) => {
        if (!countyId || !institutionToExcludeId) throw skipToken;

        return `${institutionsUrlPrefix}/religious-minority-institutions/${countyId}/${institutionToExcludeId}`;
      },
    }),
    getFirstAssignedInstitution: build.query<
      Institution,
      IGetFirstAssignedInstitutionQueryModel
    >({
      query: ({
        schoolCandidateId,
        schoolYearId,
        hasMinorityOrReligiousSchool,
      }) => {
        if (
          !schoolCandidateId ||
          !schoolYearId ||
          !hasMinorityOrReligiousSchool
        )
          throw skipToken;

        return `${institutionsUrlPrefix}/first-assigned-institution/${schoolCandidateId}/${schoolYearId}`;
      },
    }),
    getInstitutionChangeReasons: build.query<IInstitutionChangeReason[], void>({
      query: () => `${institutionsUrlPrefix}/institution-change-reasons`,
    }),
  }),
});

export const {
  useGetInstitutionDetailsQuery,
  useGetReligiousAndMinorityInstitutionsQuery,
  useGetFirstAssignedInstitutionQuery,
  useGetInstitutionChangeReasonsQuery,
} = institutionApi;
